'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { NavBar } from '@/components/Navbar';
import { SidebarProvider } from '@/components/ui/sidebar';
import Header from '@/components/header';
import RouteProtection from '@/components/RouteProtection';
import { getUserRole, ROLES, isAuthenticated } from '@/lib/auth';
import { useSyncCurrentUserRole } from '@/hooks/useUsers';
import { useRoleConfig } from '@/hooks/useRoleConfig';

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  const isAuth =
    pathname?.startsWith('/login') ||
    pathname?.startsWith('/verify-otp') ||
    (pathname?.includes('/service') && !pathname?.startsWith('/services'));

  const [isMobile, setIsMobile] = useState(false);
  const [userRole, setUserRole] = useState<(typeof ROLES)[keyof typeof ROLES] | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const shouldSyncRole = !isAuth && isAuthenticated();
  useSyncCurrentUserRole(shouldSyncRole);
  useRoleConfig(shouldSyncRole);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setIsHydrated(true);
    setUserRole(getUserRole() || ROLES.USER);
  }, []);

  useEffect(() => {
    if (isHydrated && !isAuth) {
      setUserRole(getUserRole() || ROLES.USER);
    }
  }, [pathname, isHydrated, isAuth]);

  useEffect(() => {
    if (!isHydrated || isAuth) return;

    const sync = () => setUserRole(getUserRole() || ROLES.USER);
    const onStorage = (e: StorageEvent) => { if (e.key === 'userRole') sync(); };

    window.addEventListener('storage', onStorage);
    window.addEventListener('userRoleChanged', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('userRoleChanged', sync);
    };
  }, [isHydrated, isAuth]);

  if (isAuth) return <>{children}</>;

  if (!isHydrated) return <>{children}</>;

  const isUser = (userRole || ROLES.USER) === ROLES.USER;

  return (
    <RouteProtection>
      {isUser ? (
        <div className="w-full h-screen overflow-hidden" suppressHydrationWarning>
          <main className="w-full h-full overflow-x-hidden" suppressHydrationWarning>
            {children}
          </main>
        </div>
      ) : (
        <SidebarProvider>
          <div className="flex w-full overflow-hidden bg-sidebar" suppressHydrationWarning>
            <NavBar />
            <div className="flex flex-1 flex-col overflow-hidden min-w-0">
              <div className="hidden md:block shrink-0 border-b border-sidebar-border bg-sidebar transition-colors">
                <Header />
              </div>
              <main
                className={`flex-1 p-4 transition-all duration-300 overflow-x-hidden bg-sidebar ${isMobile ? 'pt-20 pb-24' : ''}`}
                suppressHydrationWarning>
                {children}
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}
    </RouteProtection>
  );
}
